import createClient, { type FetchOptions, type Middleware } from "openapi-fetch";
import type { PathsWithMethod } from "openapi-typescript-helpers";

import { retrieveAuthToken } from "../utils";

export type FetchWrapperOptions = {
  noAuth?: boolean; // If true, the auth middleware will skip adding the Authorization header
};

export type ServiceResponse<T> = { data: T; error: null } | { data: null; error: Error };

/** Extract the operation type for a given method from a path */
type Operation<TPaths, P, Method extends string> = P extends keyof TPaths
  ? TPaths[P] extends { [K in Method]: infer Op }
    ? Op
    : never
  : never;

/** Extract the success (200) JSON response body type from an operation */
type SuccessResponseData<Op> = Op extends {
  responses: { 200: { content: { "application/json": infer D } } };
}
  ? D
  : never;

export class OpenApiFetchWrapper<TPaths extends object> {
  client: ReturnType<typeof createClient<TPaths>>;

  constructor(options?: FetchWrapperOptions) {
    this.client = createClient<TPaths>();
    if (!options?.noAuth) {
      this.client.use(authMiddleware);
    }
  }

  protected async get<P extends PathsWithMethod<TPaths, "get"> & keyof TPaths & string>(
    url: P,
    init?: FetchOptions<Operation<TPaths, P, "get">>
  ): Promise<ServiceResponse<SuccessResponseData<Operation<TPaths, P, "get">>>> {
    try {
      const response = await this.client.GET(url as any, init as any);
      if (response.data !== undefined) {
        return {
          data: response.data as SuccessResponseData<Operation<TPaths, P, "get">>,
          error: null,
        };
      }
      return {
        data: null,
        error: new Error((response.error as any)?.message || `GET ${String(url)} failed`),
      };
    } catch (error: any) {
      console.error(`Error fetching ${url}:`, error);
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  protected async post<P extends PathsWithMethod<TPaths, "post"> & keyof TPaths & string>(
    url: P,
    init?: FetchOptions<Operation<TPaths, P, "post">>
  ): Promise<ServiceResponse<SuccessResponseData<Operation<TPaths, P, "post">>>> {
    try {
      const response = await this.client.POST(url as any, init as any);
      if (response.data !== undefined) {
        return {
          data: response.data as SuccessResponseData<Operation<TPaths, P, "post">>,
          error: null,
        };
      }
      return {
        data: null,
        error: new Error((response.error as any)?.message || `POST ${String(url)} failed`),
      };
    } catch (error: any) {
      console.error(`Error posting to ${url}:`, error);
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  protected async put<P extends PathsWithMethod<TPaths, "put"> & keyof TPaths & string>(
    url: P,
    init?: FetchOptions<Operation<TPaths, P, "put">>
  ): Promise<ServiceResponse<SuccessResponseData<Operation<TPaths, P, "put">>>> {
    try {
      const response = await this.client.PUT(url as any, init as any);
      if (response.data !== undefined) {
        return {
          data: response.data as SuccessResponseData<Operation<TPaths, P, "put">>,
          error: null,
        };
      }
      return {
        data: null,
        error: new Error((response.error as any)?.message || `PUT ${String(url)} failed`),
      };
    } catch (error: any) {
      console.error(`Error putting to ${url}:`, error);
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  protected async patch<P extends PathsWithMethod<TPaths, "patch"> & keyof TPaths & string>(
    url: P,
    init?: FetchOptions<Operation<TPaths, P, "patch">>
  ): Promise<ServiceResponse<SuccessResponseData<Operation<TPaths, P, "patch">>>> {
    try {
      const response = await this.client.PATCH(url as any, init as any);
      if (response.data !== undefined) {
        return {
          data: response.data as SuccessResponseData<Operation<TPaths, P, "patch">>,
          error: null,
        };
      }
      return {
        data: null,
        error: new Error((response.error as any)?.message || `PATCH ${String(url)} failed`),
      };
    } catch (error: any) {
      console.error(`Error patching to ${url}:`, error);
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  protected async delete<P extends PathsWithMethod<TPaths, "delete"> & keyof TPaths & string>(
    url: P,
    init?: FetchOptions<Operation<TPaths, P, "delete">>
  ): Promise<ServiceResponse<SuccessResponseData<Operation<TPaths, P, "delete">>>> {
    try {
      const response = await this.client.DELETE(url as any, init as any);
      if (response.data !== undefined) {
        return {
          data: response.data as SuccessResponseData<Operation<TPaths, P, "delete">>,
          error: null,
        };
      }
      return {
        data: null,
        error: new Error((response.error as any)?.message || `DELETE ${String(url)} failed`),
      };
    } catch (error: any) {
      console.error(`Error deleting ${url}:`, error);
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }

  protected async postFormData(url: string, formData: FormData): Promise<ServiceResponse<void>> {
    try {
      const token = await retrieveAuthToken();
      const response = await fetch(url, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) {
        const msg = await response.text();
        return { data: null, error: new Error(msg) };
      }
      return { data: undefined as unknown as void, error: null };
    } catch (error: any) {
      console.error(`Error posting form data to ${url}:`, error);
      return { data: null, error: new Error(error.message || "Fetch error") };
    }
  }
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await retrieveAuthToken();
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
};
